import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useSendLoginToAuthMachine } from '@order/AuthMachine';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import type { LogInCustomerMutationVariables } from '../../GraphQL/LogIn.generated';
import { useLogInCustomerMutation } from '../../GraphQL/LogIn.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLogIn = (props?: UseLogInProps) => {
  const { onValidationError } = props ?? {};

  const sendLoginAction = useSendLoginToAuthMachine();
  const handleFailedLogIn = useLogInFailedResponse({ onValidationError });

  // ─────────────────────────────────────────────────────────────────────

  const [response, logInCustomer] = useLogInCustomerMutation();
  const { data, fetching: isLoggingIn } = response;
  const responseData = data?.login;

  // ─────────────────────────────────────────────────────────────────────

  const logIn = useCallback(
    async (input: LogInCustomerMutationVariables['input']) => {
      const logInResponse = await logInCustomer({ input });
      const logInResponseData = logInResponse.data?.login;
      const logInResponseTypeName = logInResponseData?.__typename;

      const isSuccessfullyLoggedIn = logInResponseTypeName === 'LoginSuccess';

      if (isSuccessfullyLoggedIn) {
        sendLoginAction(logInResponseData?.csrf);
      }

      if (!isSuccessfullyLoggedIn) {
        handleFailedLogIn(logInResponse);
      }

      return logInResponseData;
    },
    [handleFailedLogIn, sendLoginAction, logInCustomer],
  );

  return { logIn, isLoggingIn, responseData };
};

// ─────────────────────────────────────────────────────────────────────────────

const useLogInFailedResponse = (props: UseLogInProps) => {
  const { onValidationError } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();

  // ─── Error Handlers ──────────────────────────────────────────────────

  const onNetworkError = useCallback(
    (error: LogInMutationResponse['error']) => {
      addNoticeBanner({ text: t('general.error'), palette: 'caution' });

      track('sign-in.failure', { systemError: getUrqlError(error) });
    },
    [addNoticeBanner, t, track],
  );

  const onInvalidCredentialsError = useCallback(() => {
    const errorMessage = t('login.credentials-error');

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
    track('sign-in.failure', { userError: errorMessage });
  }, [addNoticeBanner, t, track]);

  const onLockedAccountError = useCallback(() => {
    const errorMessage = t('login.account-locked-error');

    addNoticeBanner({ text: errorMessage, palette: 'caution' });
    track('sign-in.failure', { userError: errorMessage });
  }, [addNoticeBanner, t, track]);

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(
    (failedLogInResponse: LogInMutationResponse) => {
      const { data: failedLogInResponseData, error: networkError } =
        failedLogInResponse;

      const responseTypeName = failedLogInResponseData?.login?.__typename;

      if (networkError) {
        onNetworkError(networkError);
      }

      if (responseTypeName === 'InvalidCredentials') {
        onInvalidCredentialsError();
      }

      if (responseTypeName === 'ValidationError') {
        const fieldErrors = failedLogInResponseData?.login.fieldErrors ?? [];

        onValidationError?.(fieldErrors);
      }

      if (responseTypeName === 'AccountLocked') {
        onLockedAccountError();
      }
    },
    [
      onInvalidCredentialsError,
      onLockedAccountError,
      onNetworkError,
      onValidationError,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseLogInProps = {
  readonly onValidationError?: (
    fieldErrors: LogInValidationFieldsErrors,
  ) => void;
};

type LogInMutationResponse = Awaited<
  ReturnType<ReturnType<typeof useLogInCustomerMutation>[1]>
>;

type LogInValidationFieldsErrors = ReadonlyArray<{
  readonly field: string;
  readonly message: string;
}>;
